import type { Logger } from "@hiddentao/logger"
import { decrypt, encrypt } from "../crypto/encryption"
import { hashEmail } from "../crypto/hash"
import { EventEmitter } from "../events"
import { SyncItemType } from "../sync"
import { ClientDataEvents } from "../types"
import { PresetItemType, generateId, generatePresetId } from "../utils/ids"
import {
  DEFAULT_USER_SETTINGS,
  LAST_SYNC_TIMESTAMP_KEY,
  OFFLINE_DATA_KEY,
} from "./types"
import type {
  ClientDataStorageAdapter,
  DecryptedItemData,
  Item,
  Revision,
  StoredData,
  UserSettings,
} from "./types"

// Define event map for ClientData
type ClientDataEventMap = {
  [ClientDataEvents.ITEM_UPDATED]: (
    itemId: string,
    item: Item,
    revision: Revision,
  ) => void
  [ClientDataEvents.ITEM_PLAINTEXT_UPDATED]: (
    itemId: string,
    item: Item,
    revision: Revision,
  ) => void
}

export class ClientDataStorageEngine extends EventEmitter<ClientDataEventMap> {
  private storageAdapter: ClientDataStorageAdapter
  private logger: Logger
  private data: StoredData = { items: {} }
  private decryptedCache: Record<string, DecryptedItemData> = {}
  private lastSyncTimestamp: number | null = null
  private initialized = false
  private encryptionKey: Uint8Array | null = null

  // Dynamic preset item IDs - generated per user
  private userSettingsItemId: string | null = null
  private globalTagsItemId: string | null = null

  constructor(storageAdapter: ClientDataStorageAdapter, logger: Logger) {
    super()
    this.storageAdapter = storageAdapter
    this.logger = logger.child("ClientData")
  }

  /**
   * Get the user settings item ID (must be called after initialization)
   */
  public getUserSettingsItemId(): string {
    if (!this.userSettingsItemId) {
      throw new Error(
        "User settings item ID not available - call initialize() first",
      )
    }
    return this.userSettingsItemId
  }

  /**
   * Get the global tags item ID (must be called after initialization)
   */
  public getGlobalTagsItemId(): string {
    if (!this.globalTagsItemId) {
      throw new Error(
        "Global tags item ID not available - call initialize() first",
      )
    }
    return this.globalTagsItemId
  }

  /**
   * Initialize the client data storage with encryption key and email
   * @param encryptionKey The encryption key to use for all operations
   * @param email The user's email address (used to generate storage key)
   * @throws Error if encryption key or email is not provided
   */
  public async initialize(
    encryptionKey: Uint8Array,
    email: string,
  ): Promise<void> {
    if (this.initialized) return

    // Set encryption key
    if (!encryptionKey) {
      throw new Error("Encryption key is required")
    }
    if (!email) {
      throw new Error("Email is required")
    }
    this.encryptionKey = encryptionKey

    // Generate dynamic preset item IDs based on user email
    this.userSettingsItemId = await generatePresetId(
      PresetItemType.USER_SETTINGS,
      email,
    )
    this.logger.debug("User settings item ID", {
      id: this.userSettingsItemId,
    })

    this.globalTagsItemId = await generatePresetId(
      PresetItemType.GLOBAL_TAGS,
      email,
    )
    this.logger.debug("Global tags item ID", {
      id: this.globalTagsItemId,
    })

    // Hash email and set as storage key
    const hashedEmail = await hashEmail(email)
    this.storageAdapter.storageKey = hashedEmail

    // Load last sync timestamp
    const storedTimestamp = await this.storageAdapter.getItem(
      LAST_SYNC_TIMESTAMP_KEY,
    )
    if (storedTimestamp) {
      this.lastSyncTimestamp = parseInt(storedTimestamp, 10)
    }

    // Load offline data
    const storedData = await this.storageAdapter.getItem(OFFLINE_DATA_KEY)
    if (storedData) {
      try {
        this.data = JSON.parse(storedData) as StoredData
      } catch (error) {
        this.logger.error("Failed to parse offline data", { error })
      }
    }
    this.initialized = true
  }

  public async clear(): Promise<void> {
    this.data = { items: {} }
    this.decryptedCache = {}
    this.lastSyncTimestamp = null
    this.userSettingsItemId = null
    this.globalTagsItemId = null
    await this.storageAdapter.removeItem(LAST_SYNC_TIMESTAMP_KEY)
    await this.storageAdapter.removeItem(OFFLINE_DATA_KEY)
  }

  /**
   * Ensures encryption key is set and returns it
   * @throws Error if encryption key is not set
   */
  private ensureEncryptionKey(): Uint8Array {
    if (!this.encryptionKey) {
      throw new Error("Encryption key is not set")
    }
    return this.encryptionKey
  }

  /**
   * Helper method to add a revision to an item
   */
  private addRevisionToItem(item: Item, revision: Revision): Item {
    return {
      ...item,
      revisions: [...item.revisions, revision],
    }
  }

  /**
   * Helper method to update or add an item to the data store
   */
  private updateItemInStore(item: Item): void {
    const newItems = { ...this.data.items }
    newItems[item.id] = item
    this.data = { ...this.data, items: newItems }
  }

  /**
   * Helper method to decrypt and cache item data
   */
  private async decryptAndCacheItemData(
    itemId: string,
    encryptedData: string,
  ): Promise<DecryptedItemData> {
    const encryptionKey = this.ensureEncryptionKey()

    try {
      const decryptedData = await decrypt(encryptedData, encryptionKey)
      const parsedData = JSON.parse(decryptedData)
      this.decryptedCache[itemId] = parsedData
      return parsedData
    } catch (error) {
      throw new Error(`Failed to decrypt item data for ${itemId}: ${error}`)
    }
  }

  /**
   * Helper method for committing item changes and emitting events
   */
  private async commitItemChange(
    item: Item,
    revision: Revision,
  ): Promise<void> {
    await this.persistData()
    this.emit(ClientDataEvents.ITEM_UPDATED, item.id, item, revision)
  }

  /**
   * Update an item with unencrypted data, handling encryption and item creation if necessary
   * @param itemId The ID of the item to update
   * @param type The type of the item
   * @param plaintextData Any data to store in the item
   * @returns The updated item and revision
   */
  public async updateItemPlaintext(
    itemId: string,
    type: string,
    plaintextData: any,
  ): Promise<{ item: Item; revision: Revision }> {
    // Item ID is required
    if (!itemId) {
      throw new Error("Item ID is required")
    }

    // Ensure encryption key exists (will throw if not set)
    const encryptionKey = this.ensureEncryptionKey()
    const now = new Date().toISOString()

    // Get existing item or create a new base item if it doesn't exist
    const existingItem = this.data.items[itemId]
    const item: Item = existingItem
      ? {
          ...existingItem,
          updatedAt: now,
        }
      : {
          id: itemId,
          type,
          createdAt: now,
          updatedAt: now,
          revisions: [],
        }

    // Store the plaintext data in cache directly
    this.decryptedCache[itemId] = plaintextData

    // Convert plaintext data to JSON and encrypt
    const plaintextDataString = JSON.stringify(plaintextData)
    const encryptedData = await encrypt(plaintextDataString, encryptionKey)

    // Determine if we should create a new revision or update an existing one
    let revision: Revision
    let updatedItem: Item

    if (item.revisions.length > 0) {
      // Get the latest revision and update its encrypted data
      const latestRevision = item.revisions[item.revisions.length - 1]
      revision = {
        ...latestRevision,
        encryptedData, // Update the encrypted data
      }

      // Replace the latest revision with the updated one
      const revisionsWithoutLatest = item.revisions.slice(0, -1)
      updatedItem = {
        ...item,
        revisions: [...revisionsWithoutLatest, revision],
      }
    } else {
      // Create a new revision (no previous revision exists)
      revision = {
        id: generateId(),
        itemId,
        encryptedData,
        createdAt: now,
      }

      // Add the revision to the item
      updatedItem = this.addRevisionToItem(item, revision)
    }

    // Update the store
    this.updateItemInStore(updatedItem)

    // Persist changes and emit event
    await this.commitItemChange(updatedItem, revision)

    // Emit plaintext updated event
    this.emit(
      ClientDataEvents.ITEM_PLAINTEXT_UPDATED,
      itemId,
      updatedItem,
      revision,
    )

    // Return the saved item and revision
    return {
      item: updatedItem,
      revision,
    }
  }

  /**
   * Get an item by ID
   */
  public getItem(id: string): Item | null {
    return this.data.items[id] || null
  }

  /**
   * Get all items
   */
  public getAllItems(): Record<string, Item> {
    return this.data.items
  }

  /**
   * Get items by type
   * @param type The type of items to fetch
   * @returns A record of items with the specified type
   */
  public getItemsByType(type: SyncItemType): Record<string, Item> {
    const filteredItems: Record<string, Item> = {}

    Object.entries(this.data.items).forEach(([id, item]) => {
      if (item.type === type) {
        filteredItems[id] = item
      }
    })

    return filteredItems
  }

  /**
   * Get decrypted data for an item
   */
  public async getDecryptedItemData(
    itemId: string,
  ): Promise<DecryptedItemData | null> {
    // If we have cached decrypted data, return it
    if (this.decryptedCache[itemId]) {
      return this.decryptedCache[itemId]
    }

    const item = this.getItem(itemId)
    if (!item || !item.revisions.length) {
      return null
    }

    // Get the latest revision
    const latestRevision = item.revisions[item.revisions.length - 1]

    // Decrypt and cache the data
    try {
      return await this.decryptAndCacheItemData(
        itemId,
        latestRevision.encryptedData,
      )
    } catch (error) {
      this.logger.error("Failed to decrypt item data", { error })
      throw new Error(`Failed to decrypt item data: ${error}`)
    }
  }

  /**
   * Get user settings from the designated settings item
   * @returns User settings object, or default settings if none found
   */
  public async getUserSettings(): Promise<UserSettings> {
    if (!this.userSettingsItemId) {
      throw new Error(
        "User settings item ID not available - call initialize() first",
      )
    }

    try {
      const settings = (await this.getDecryptedItemData(
        this.userSettingsItemId,
      )) as UserSettings | null
      return settings || DEFAULT_USER_SETTINGS
    } catch (error) {
      this.logger.error("Failed to get user settings", { error })
      return DEFAULT_USER_SETTINGS
    }
  }

  /**
   * Update user settings
   * @param settings The settings object to save
   * @returns The updated settings
   */
  public async updateUserSettings(
    settings: UserSettings,
  ): Promise<UserSettings> {
    if (!this.userSettingsItemId) {
      throw new Error(
        "User settings item ID not available - call initialize() first",
      )
    }

    try {
      // Merge with existing settings if they exist
      const existingSettings = await this.getUserSettings()
      const mergedSettings = { ...existingSettings, ...settings }

      // Save the merged settings to the preset settings item
      await this.updateItemPlaintext(
        this.userSettingsItemId,
        SyncItemType.UserPreference,
        mergedSettings,
      )

      return mergedSettings
    } catch (error) {
      this.logger.error("Failed to update user settings", { error })
      throw new Error(`Failed to update user settings: ${error}`)
    }
  }

  /**
   * Update items received from the server, handling decryption
   */
  public async updateItemsFromServer(items: Item[]): Promise<void> {
    const newItems = { ...this.data.items }

    for (const item of items) {
      if (newItems[item.id]) {
        // Merge with existing item
        newItems[item.id] = {
          ...newItems[item.id],
          ...item,
          revisions: [
            ...(newItems[item.id].revisions || []),
            ...item.revisions,
          ],
        }
      } else {
        // Add new item
        newItems[item.id] = item
      }

      // Try to decrypt the latest revision's data
      if (item.revisions.length > 0) {
        const latestRevision = item.revisions[item.revisions.length - 1]
        try {
          await this.decryptAndCacheItemData(
            item.id,
            latestRevision.encryptedData,
          )
        } catch (error) {
          throw new Error(`Failed to decrypt item data during update: ${error}`)
        }
      }
    }

    this.data = { ...this.data, items: newItems }
    await this.persistData()
  }

  public getLastSyncTimestamp(): number {
    return this.lastSyncTimestamp || 0
  }

  public async setLastSyncTimestamp(timestamp: number): Promise<void> {
    this.lastSyncTimestamp = timestamp
    await this.storageAdapter.setItem(
      LAST_SYNC_TIMESTAMP_KEY,
      timestamp.toString(),
    )
  }

  private async persistData(): Promise<void> {
    await this.storageAdapter.setItem(
      OFFLINE_DATA_KEY,
      JSON.stringify(this.data),
    )
  }
}
